import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import api, { API } from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Loader2, Sparkles, Save, Download, RefreshCw, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";

// DOMPurify config: only the tags our tiny markdown renderer emits. Anything
// else (script/img/iframe/on*) is stripped. Belt-and-suspenders even though
// `inline()` already escapes raw HTML — user content is untrusted.
const SANITIZE_CFG = { ALLOWED_TAGS: ["strong", "em"], ALLOWED_ATTR: [] };
const sanitize = (html) => DOMPurify.sanitize(html, SANITIZE_CFG);

/**
 * Minimal inline markdown renderer. Handles headings (#, ##, ###), bullets (- or *),
 * numbered lists, **bold**, *italic*, _italic_, and paragraphs.
 * Kept dependency-free on purpose.
 */
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let para = [];
  let bullets = [];

  const inline = (s) => {
    // escape angle brackets so user-typed <> doesn't render as HTML.
    let t = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    t = t.replace(/(^|[^*])\*(?!\*)([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");
    t = t.replace(/(^|[^_])_(?!_)([^_\n]+?)_(?!_)/g, "$1<em>$2</em>");
    return t;
  };

  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: "p", text: para.join(" ").trim() });
      para = [];
    }
  };
  const flushBullets = () => {
    if (bullets.length) {
      blocks.push({ type: "ul", items: bullets.slice() });
      bullets = [];
    }
  };

  lines.forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      flushBullets();
      return;
    }
    let m;
    if ((m = line.match(/^###\s+(.*)$/))) {
      flushPara();
      flushBullets();
      blocks.push({ type: "h3", text: m[1] });
    } else if ((m = line.match(/^##\s+(.*)$/))) {
      flushPara();
      flushBullets();
      blocks.push({ type: "h2", text: m[1] });
    } else if ((m = line.match(/^#\s+(.*)$/))) {
      flushPara();
      flushBullets();
      blocks.push({ type: "h1", text: m[1] });
    } else if ((m = line.match(/^\s*[-*]\s+(.*)$/))) {
      flushPara();
      bullets.push(m[1]);
    } else if ((m = line.match(/^\s*\d+\.\s+(.*)$/))) {
      flushPara();
      bullets.push(m[1]);
    } else {
      flushBullets();
      para.push(line.trim());
    }
  });
  flushPara();
  flushBullets();

  return blocks.map((b, i) => {
    const bKey = `${b.type}-${i}`;
    if (b.type === "h1") return <h2 key={bKey} className="font-display text-2xl mt-6 mb-3 text-foreground">{b.text}</h2>;
    if (b.type === "h2") return <h3 key={bKey} className="font-sans-ui text-base uppercase tracking-widest text-accent mt-6 mb-2">{b.text}</h3>;
    if (b.type === "h3") return <h4 key={bKey} className="font-sans-ui font-semibold text-sm mt-4 mb-1 text-foreground">{b.text}</h4>;
    if (b.type === "ul") {
      // Sanitized HTML pre-computed OUTSIDE the JSX so DOMPurify runs once
      // per item and no `{__html: ...}` literal sits inside the render path.
      const itemsHtml = b.items.map((it) => sanitize(inline(it)));
      return (
        <ul key={bKey} className="list-disc pl-5 space-y-1 my-2">
          {itemsHtml.map((html, j) => {
            const props = { __html: html };
            return (
              <li key={`${bKey}-item-${j}`} className="text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={props} />
            );
          })}
        </ul>
      );
    }
    const paragraphProps = { __html: sanitize(inline(b.text)) };
    return (
      <p key={bKey} className="text-sm text-muted-foreground leading-relaxed my-2" dangerouslySetInnerHTML={paragraphProps} />
    );
  });
}

export default function ProposalPanel({ open, lead, onOpenChange }) {
  const [content, setContent] = useState("");
  const [generatedAt, setGeneratedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [autoGenerated, setAutoGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("edit"); // edit | preview
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    setLoading(true);
    setMode("edit");
    setDirty(false);
    api
      .get(`/leads/${lead.id}/proposal`)
      .then((r) => {
        setContent(r.data.content || "");
        setGeneratedAt(r.data.generated_at || null);
        setUpdatedAt(r.data.updated_at || null);
        setAutoGenerated(Boolean(r.data.auto_generated));
      })
      .catch(() => toast.error("Failed to load proposal"))
      .finally(() => setLoading(false));
  }, [open, lead]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/proposal/generate`);
      setContent(data.content || "");
      setGeneratedAt(data.generated_at);
      setUpdatedAt(data.updated_at);
      setAutoGenerated(false); // manual regen wipes the auto flag
      setDirty(false);
      setMode("preview");
      toast.success("Proposal generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}/proposal`, { content });
      setUpdatedAt(data.updated_at);
      setDirty(false);
      toast.success("Proposal saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = () => {
    if (dirty) {
      toast("Saving before export…");
      save().then(() => {
        window.open(`${API}/leads/${lead.id}/proposal/pdf`, "_blank");
      });
      return;
    }
    window.open(`${API}/leads/${lead.id}/proposal/pdf`, "_blank");
  };

  const onChange = (v) => {
    setContent(v);
    setDirty(true);
  };

  const hasProposal = (content || "").trim().length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl overflow-y-auto"
        data-testid="proposal-panel"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">
            Proposal · <span className="text-accent">{lead?.company}</span>
          </SheetTitle>
          <SheetDescription>
            {lead?.country} · {lead?.industry} · {lead?.function} · Score {lead?.results?.overall_score ?? "—"}/100
          </SheetDescription>
          {autoGenerated && hasProposal && (
            <div
              data-testid="proposal-auto-drafted-badge"
              className="mt-2 inline-flex items-center gap-1.5 self-start text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-accent/15 border border-accent/40 text-accent"
              title="This proposal was auto-drafted by AI when the lead was captured. Edits you make are kept."
            >
              <Sparkles className="w-3 h-3" /> Auto-drafted
            </div>
          )}
        </SheetHeader>

        {loading ? (
          <div className="py-20 flex flex-col items-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
            <div className="mt-3 text-sm">Loading proposal…</div>
          </div>
        ) : !hasProposal ? (
          <div className="mt-10 border border-dashed border-border/60 rounded-md p-10 text-center" data-testid="proposal-empty">
            <Sparkles className="w-7 h-7 text-accent mx-auto" />
            <h3 className="font-sans-ui font-semibold mt-4">Generate a tailored proposal</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              We&rsquo;ll use the lead&rsquo;s answers, gaps, score, and your profile to draft a scope, timeline, and CTA — ready for you to edit.
            </p>
            <Button
              onClick={generate}
              disabled={generating}
              data-testid="proposal-generate-btn"
              className="mt-6 bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? "Generating…" : "Generate proposal"}
            </Button>
          </div>
        ) : (
          <div className="mt-5">
            <div className="flex items-center justify-between flex-wrap gap-2 border-b border-border/60 pb-3">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                {updatedAt ? `Updated ${new Date(updatedAt).toLocaleString()}` : ""}
                {dirty && <span className="ml-2 text-accent">• unsaved</span>}
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-full border border-border/60 overflow-hidden text-xs" data-testid="proposal-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setMode("edit")}
                    data-testid="proposal-mode-edit"
                    className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${mode === "edit" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("preview")}
                    data-testid="proposal-mode-preview"
                    className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${mode === "preview" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    <Eye className="w-3 h-3" /> Preview
                  </button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={generate}
                  disabled={generating}
                  data-testid="proposal-regen-btn"
                  title="Regenerate from scratch"
                >
                  {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={save}
                  disabled={saving || !dirty}
                  data-testid="proposal-save-btn"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                  Save
                </Button>
                <Button
                  size="sm"
                  onClick={exportPdf}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="proposal-export-pdf-btn"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF
                </Button>
              </div>
            </div>

            {mode === "edit" ? (
              <Textarea
                data-testid="proposal-editor"
                value={content}
                onChange={(e) => onChange(e.target.value)}
                className="mt-4 min-h-[60vh] font-mono text-sm leading-relaxed"
                spellCheck={true}
              />
            ) : (
              <div className="mt-4 prose-bp" data-testid="proposal-preview">
                {renderMarkdown(content)}
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-4">
              Tip: Markdown supported — <code>##</code> headings, <code>-</code> bullets, <code>**bold**</code>. The PDF export renders the same way.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
